import * as qs from 'query-string';
import * as moment from 'moment';
import { useEffect, useReducer } from 'react';
import { ICprScoreAuditResponse } from '@nkr/store/index';
import { initialState, reducer } from './reducer';

interface IOptions {
    centerId: number,
    reportPath: string,
    endDateParam: string,
    allowCustomRange: boolean,
    loadingMessage: string,
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getScoreAudit: (startDate: string, endDate: string) => Promise<ICprScoreAuditResponse>
}

export const useScoreAudit = ({ centerId, reportPath, endDateParam, allowCustomRange, loadingMessage, showLoading, hideLoading, getScoreAudit }: IOptions) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        setUrlParameters();
    }, [state.reportStart, state.reportEnd, state.included, state.favorable, state.compatible, state.transplanted]);

    const getData = () => {
        let parsed = qs.parse(location.search);

        let startDate = moment(parsed?.startDate, 'YYYY-MM-DD', true);
        let endDate = moment(parsed?.[endDateParam], 'YYYY-MM-DD', true);

        if (!endDate.isValid()) {
            endDate = moment(state.reportEnd, 'YYYY-MM-DD', true);
        }

        let included = true;
        let favorable = false;
        let compatible = false;
        let transplanted: boolean = null;

        if (parsed?.included) {
            included = parseInt(parsed.included) == 1;
        }

        if (parsed?.favorable) {
            favorable = parseInt(parsed.favorable) == 1;
        }

        if (parsed?.compatible) {
            compatible = parseInt(parsed.compatible) == 1;
        }

        if (parsed?.transplanted) {
            transplanted = parseInt(parsed.transplanted) == 1;
        }

        let customRange = allowCustomRange && startDate.isValid();
        let startDateStr = customRange ? startDate.format('YYYY-MM-DD') : '';
        let endDateStr = endDate.format('YYYY-MM-DD');

        showLoading(loadingMessage).then(() => {
            getScoreAudit(startDateStr, endDateStr).then(response => {
                let { reportData: data, center, reportStart, reportEnd } = response.data.results;

                dispatch({
                    type: 'afterGetData',
                    payload: {
                        data,
                        center,
                        reportStart,
                        newReportStart: reportStart,
                        reportEnd,
                        newReportEnd: reportEnd,
                        customRange,
                        included,
                        favorable,
                        compatible,
                        transplanted
                    }
                });

                hideLoading();
            });
        });
    };

    const refreshData = (startDate: string, endDate: string) => {
        showLoading(loadingMessage).then(() => {
            getScoreAudit(startDate, endDate).then(response => {
                let { reportData: data, center, reportStart, reportEnd } = response.data.results;

                dispatch({
                    type: 'refreshData',
                    payload: {
                        data,
                        center,
                        reportStart,
                        newReportStart: reportStart,
                        reportEnd,
                        newReportEnd: reportEnd
                    }
                });

                hideLoading();
            });
        });
    };

    const setUrlParameters = () => {
        let paramList = [];

        if (state.customRange) {
            paramList.push(encodeURI('startDate=' + state.reportStart));
        }

        paramList.push(encodeURI(endDateParam + '=' + state.reportEnd));
        paramList.push(encodeURI('included=' + (state.included ? '1' : '0')));
        paramList.push(encodeURI('favorable=' + (state.favorable ? '1' : '0')));
        paramList.push(encodeURI('compatible=' + (state.compatible ? '1' : '0')));

        if (state.transplanted != null) {
            paramList.push(encodeURI('transplanted=' + (state.transplanted ? '1' : '0')));
        }

        let params = paramList.join('&');

        let newUrl = '/center/' + centerId + '/reports/my-center/' + reportPath + '?' + params

        window.history.pushState({}, '', newUrl);
    };

    const getFilteredData = () => {
        return state.data.filter(x => {
            let filter = true;
            let pairFilter = (!state.favorable || x.isFavorablePair) && (!state.compatible || x.isCompatiblePair);
            let hasPairFilter = state.favorable || state.compatible;

            if (state.included && !hasPairFilter) {
                filter = x.isIncluded;
            }

            if (!state.included && hasPairFilter) {
                filter = pairFilter;
            }

            if (state.included && hasPairFilter) {
                filter = x.isIncluded || pairFilter;
            }

            if (state.transplanted != null) {
                filter = filter && x.isTranplanted == state.transplanted && moment(x.transplantDate) > moment(state.reportStart);
            }

            return filter;
        });
    };

    const handleFilterToggle = (name: string, value: boolean) => {
        dispatch({
            type: 'updateField',
            payload: {
                name,
                value
            }
        });
    };

    const handlePairTotalClick = (favorable: boolean) => {
        dispatch({
            type: 'compatibleOrIncludedTotalOnly',
            payload: {
                included: false,
                favorable,
                compatible: true,
                transplanted: null
            }
        });
    };

    const handleIncludedTotalClick = () => {
        dispatch({
            type: 'compatibleOrIncludedTotalOnly',
            payload: {
                included: true,
                favorable: false,
                compatible: false,
                transplanted: null
            }
        })
    };

    const handleReportStartChange = (newReportStart: string) => {
        dispatch({
            type: 'updateField',
            payload: {
                name: 'newReportStart',
                value: newReportStart
            }
        });
    };

    const handleReportEndChange = (newReportEnd: string) => {
        dispatch({
            type: 'updateField',
            payload: {
                name: 'newReportEnd',
                value: newReportEnd
            }
        });
    };

    const handleRefresh = () => {
        refreshData(state.customRange ? state.newReportStart : '', state.newReportEnd);
    };

    const handleToggleHelp = () => {
        dispatch({
            type: 'updateField',
            payload: {
                name: 'expandHelp',
                value: !state.expandHelp
            }
        });
    };

    const data = getFilteredData();

    return {
        state,
        data,
        includedCount: data.filter(x => x.isIncluded).length,
        compatibleCount: data.filter(x => x.isCompatiblePair && x.isIncluded && (state.transplanted != null ? x.isTranplanted == state.transplanted : true)).length,
        favorableCount: data.filter(x => x.isFavorablePair && x.isIncluded).length,
        transplantedCount: data.filter(x => x.isTranplanted).length,
        summaryIncluded: state.data.filter(x => x.isIncluded).length,
        handleFilterToggle,
        handlePairTotalClick,
        handleIncludedTotalClick,
        handleReportStartChange,
        handleReportEndChange,
        handleRefresh,
        handleToggleHelp
    };
};
